<template>
  <v-dialog :value="value" max-width="720" persistent scrollable @input="onDialogInput">
    <v-card class="analysis-group-dialog">
      <v-card-title class="px-6">
        <h5 class="primary-color mb-0">{{ isEdit ? 'Edit group' : 'New analysis group' }}</h5>
        <v-spacer />
        <v-btn icon aria-label="Close" :disabled="saving" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-subtitle class="px-6 pt-1">
        A named experiment: one configuration version and one set of engines applied to the videos
        of the group. Runs never change the active run of a video.
      </v-card-subtitle>

      <v-card-text class="px-6 py-2">
        <v-form ref="form" @submit.prevent="save">
          <v-text-field
            v-model="form.name"
            label="Name *"
            dense
            outlined
            counter="120"
            maxlength="120"
            hide-details="auto"
            class="mb-3 analysis-group-dialog__name"
            :rules="[rules.required, rules.maxName]"
            :disabled="saving"
            autofocus
          />
          <v-textarea
            v-model="form.description"
            label="Description"
            rows="2"
            auto-grow
            dense
            outlined
            hide-details="auto"
            class="mb-4"
            :disabled="saving"
          />

          <v-alert v-if="frozen" dense text type="info" class="mb-4 analysis-group-dialog__frozen">
            <v-icon small left>mdi-snowflake</v-icon>
            Frozen since {{ formatDate(group.frozen_at) }}: configuration and engines can no longer
            change. Name, description and threshold stay editable.
          </v-alert>

          <div class="analysis-group-dialog__section">Configuration</div>
          <v-select
            v-model="form.configId"
            label="Analysis configuration *"
            :items="analysisTypes"
            item-text="name"
            item-value="id"
            :loading="loadingAnalysisTypes"
            :disabled="frozen || saving || loadingAnalysisTypes"
            dense
            outlined
            hide-details="auto"
            class="mb-3 analysis-group-dialog__config"
            :rules="[(v) => !!v || 'Select a configuration']"
            attach
          />
          <v-progress-linear v-if="loadingVersions" indeterminate color="primary" class="mb-2" />
          <v-radio-group
            v-model="form.configVersionId"
            dense
            hide-details
            class="mt-0 mb-4 analysis-group-dialog__versions"
            :disabled="frozen || saving"
          >
            <v-radio :value="null" label="Current version (pinned when the group is created)" />
            <v-radio
              v-for="version in versions"
              :key="version.id"
              :value="version.id"
              :label="versionLabel(version)"
            />
          </v-radio-group>

          <div class="analysis-group-dialog__section">Engines</div>
          <EngineSelector
            v-model="form.engines"
            :source-type="sourceType"
            :disabled="frozen || saving"
            :default-all="!isEdit"
            @change="onEnginesChange"
            @catalogue-loaded="onCatalogueLoaded"
          />
          <p
            v-if="showEnginesError"
            class="error--text caption mb-2 analysis-group-dialog__engines-error"
            role="alert"
          >
            Select at least one engine.
          </p>
          <p v-if="selection.auto_added.length" class="caption primary--text mt-2 mb-3">
            Added automatically: {{ selection.auto_added.map(labelOf).join(', ') }}
          </p>

          <div class="analysis-group-dialog__section mt-2">Evaluation</div>
          <v-text-field
            v-model="form.thresholdPct"
            label="Subtraction threshold (%)"
            type="number"
            min="0"
            max="100"
            dense
            outlined
            hide-details="auto"
            hint="Optional; leaves the configuration's own threshold when empty"
            persistent-hint
            class="mb-3 analysis-group-dialog__threshold"
            :rules="[rules.threshold]"
            :disabled="saving"
          />
          <v-switch
            v-if="!frozen"
            v-model="form.freeze"
            inset
            dense
            color="primary"
            hide-details
            class="mt-1 analysis-group-dialog__freeze"
            :label="isEdit ? 'Freeze configuration and engines now' : 'Freeze right after creating'"
            :disabled="saving"
          />
          <p class="caption grey--text text--darken-1 mt-1 mb-0">
            A group is frozen automatically on its first run so every batch is comparable.
          </p>
        </v-form>

        <v-alert v-if="error" dense text type="error" class="mt-3 mb-0">{{ error }}</v-alert>
      </v-card-text>

      <v-divider />
      <v-card-actions class="px-6 py-3">
        <span class="caption grey--text text--darken-2 analysis-group-dialog__summary">
          {{ summaryLine }}
        </span>
        <v-spacer />
        <v-btn text class="cancel-modal" :disabled="saving" @click="close">Cancel</v-btn>
        <v-btn
          class="ok-modal analysis-group-dialog__save"
          :disabled="!canSave"
          :loading="saving"
          @click="save"
        >
          {{ isEdit ? 'Save changes' : 'Create group' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import EngineSelector from '@/components/analysis/EngineSelector.vue';
import AnalysisGroupsService from '@/services/AnalysisGroupsService';
import InterviewsDataService from '@/services/InterviewsDataService';
import { engineLabel, formatLaunchSummary } from '@/utils/engineRules';

const EMPTY_SELECTION = Object.freeze({
  engines: [],
  requested: [],
  auto_added: [],
  warnings: [],
  valid: false,
  error: 'engines_empty',
});

/**
 * Create / edit an analysis group (M4 contract 2): name, description, configuration + version,
 * engines (EngineSelector, coherence pulls gestures in), optional threshold and freeze.
 * Once frozen only name, description and threshold are sent. Emits `saved` with the group.
 */
export default {
  name: 'AnalysisGroupDialog',

  components: { EngineSelector },

  props: {
    value: { type: Boolean, default: false },
    /** Group to edit (null = create). */
    group: { type: Object, default: null },
    /** [{ id, name }] analysis configurations. */
    analysisTypes: { type: Array, default: () => [] },
    loadingAnalysisTypes: { type: Boolean, default: false },
    /** Source type used to validate the engines ('interview' when the group holds interviews). */
    sourceType: { type: String, default: 'manual_analysis' },
  },

  data() {
    return {
      form: this.emptyForm(),
      versions: [],
      loadingVersions: false,
      selection: EMPTY_SELECTION,
      catalogue: [],
      saving: false,
      error: '',
      touched: false,
      rules: {
        required: (v) => (typeof v === 'string' && v.trim().length > 0) || 'Name is required',
        maxName: (v) => !v || v.length <= 120 || 'Max 120 characters',
        threshold: (v) => {
          if (v === '' || v === null || v === undefined) return true;
          const n = Number(v);
          return (Number.isFinite(n) && n >= 0 && n <= 100) || 'Enter a value between 0 and 100';
        },
      },
    };
  },

  computed: {
    isEdit() {
      return Boolean(this.group && this.group.id != null);
    },
    frozen() {
      return Boolean(this.group && this.group.frozen_at);
    },
    selectedType() {
      return (
        this.analysisTypes.find((type) => Number(type.id) === Number(this.form.configId)) || null
      );
    },
    selectedVersion() {
      if (!this.form.configVersionId) return null;
      return this.versions.find((version) => version.id === this.form.configVersionId) || null;
    },
    nameValid() {
      return typeof this.form.name === 'string' && this.form.name.trim().length > 0;
    },
    thresholdValid() {
      return this.rules.threshold(this.form.thresholdPct) === true;
    },
    showEnginesError() {
      return this.touched && !this.frozen && !this.selection.valid;
    },
    canSave() {
      if (this.saving || !this.nameValid || !this.thresholdValid) return false;
      if (this.frozen) return true;
      return Boolean(this.form.configId) && this.selection.valid;
    },
    summaryLine() {
      return formatLaunchSummary({
        engines: this.selection.engines,
        configName: this.selectedType?.name || null,
        configVersion: this.selectedVersion?.version ?? this.group?.config_version ?? null,
      });
    },
  },

  watch: {
    value(open) {
      if (open) this.reset();
    },
    'form.configId'(id, previous) {
      if (previous !== undefined && previous !== null && id !== previous) {
        this.form.configVersionId = null;
      }
      if (id) this.loadVersions(id);
      else this.versions = [];
    },
  },

  created() {
    if (this.value) this.reset();
  },

  methods: {
    emptyForm() {
      return {
        name: '',
        description: '',
        configId: null,
        configVersionId: null,
        engines: [],
        thresholdPct: '',
        freeze: false,
      };
    },

    reset() {
      this.saving = false;
      this.error = '';
      this.touched = false;
      this.selection = EMPTY_SELECTION;
      const group = this.group;
      this.form = {
        name: group?.name || '',
        description: group?.description || '',
        configId:
          group?.interview_analysis_config_id ??
          group?.config_id ??
          this.analysisTypes[0]?.id ??
          null,
        configVersionId: group?.gestures_mongo_config_id ?? group?.config_version_id ?? null,
        engines: Array.isArray(group?.engines) ? [...group.engines] : [],
        thresholdPct: group?.threshold_pct == null ? '' : String(group.threshold_pct),
        freeze: false,
      };
      if (this.form.configId) this.loadVersions(this.form.configId);
    },

    labelOf(name) {
      return engineLabel(this.catalogue, name);
    },

    async loadVersions(id) {
      this.loadingVersions = true;
      try {
        const response = await InterviewsDataService.getConfigVersions(id);
        const items = response?.data?.data ?? response?.data;
        this.versions = Array.isArray(items) ? items : [];
      } catch (error) {
        this.versions = [];
      } finally {
        this.loadingVersions = false;
      }
    },

    versionLabel(version) {
      const parts = [`v${version.version}`];
      if (version.created_at) parts.push(this.formatDate(version.created_at));
      if (version.notes) parts.push(version.notes);
      if (version.used_by_runs) parts.push(`used by ${version.used_by_runs} runs`);
      return parts.join(' · ');
    },

    formatDate(value) {
      if (!value) return '';
      const date = new Date(value);
      return Number.isFinite(date.getTime()) ? date.toLocaleDateString() : String(value);
    },

    onEnginesChange(result) {
      this.selection = result || EMPTY_SELECTION;
      this.touched = this.touched || Boolean(result && result.requested && result.requested.length);
    },

    onCatalogueLoaded(catalogue) {
      this.catalogue = Array.isArray(catalogue) ? catalogue : [];
    },

    onDialogInput(open) {
      if (!open) this.close();
    },

    close() {
      if (this.saving) return;
      this.$emit('input', false);
    },

    /** Body for POST / PUT. SQL column names plus the short aliases used by the run options. */
    buildPayload() {
      const threshold =
        this.form.thresholdPct === '' || this.form.thresholdPct === null
          ? null
          : Number(this.form.thresholdPct);
      const payload = {
        name: this.form.name.trim(),
        description: this.form.description.trim() || null,
        threshold_pct: threshold,
      };
      if (!this.frozen) {
        payload.interview_analysis_config_id = Number(this.form.configId);
        payload.config_id = Number(this.form.configId);
        payload.gestures_mongo_config_id = this.form.configVersionId || null;
        payload.config_version_id = this.form.configVersionId || null;
        payload.engines = [...this.selection.requested];
      }
      return payload;
    },

    async save() {
      this.touched = true;
      if (!this.canSave) return;
      this.saving = true;
      this.error = '';
      try {
        const payload = this.buildPayload();
        const response = this.isEdit
          ? await AnalysisGroupsService.update(this.group.id, payload)
          : await AnalysisGroupsService.create(payload);
        let saved = response?.data?.data ?? response?.data ?? {};
        if (this.form.freeze && !this.frozen && saved.id != null) {
          const frozen = await AnalysisGroupsService.freeze(saved.id);
          saved = frozen?.data?.data ?? frozen?.data ?? saved;
        }
        this.$emit('saved', saved);
        this.$emit('input', false);
      } catch (error) {
        const data = error?.response?.data;
        this.error = data?.message || data?.error || error?.message || 'Could not save the group.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.analysis-group-dialog__section {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #757575;
  margin-bottom: 8px;
}
.analysis-group-dialog__summary {
  flex: 1 1 220px;
  min-width: 0;
  overflow-wrap: anywhere;
}
</style>
